import { useState, useEffect, useCallback } from 'react'
import { mcpAuth } from '../services/mcpAuth'
import { getGoogleToken, isGoogleConnected } from '../services/googleAuth'
import { isSlackConnected, slack } from '../services/slack'
export interface HealthHistoryEntry {
  timestamp: number
  status: 'connected' | 'degraded' | 'disconnected' | 'checking' | 'unknown'
  latency: number | null
}
export interface ConnectorHealthResult {
  connectorId: string
  name: string
  status: 'connected' | 'degraded' | 'disconnected' | 'checking' | 'unknown'
  latency: number | null // ms
  lastChecked: number | null // timestamp
  tokenExpiresIn: string | null // '47 min' or 'N/A'
  lastError: string | null
  history: HealthHistoryEntry[]
}
const STORAGE_KEY = 'vsm_health_check_history'
const INITIAL_CONNECTORS: ConnectorHealthResult[] = [
  {
    connectorId: 'api',
    name: 'API Gateway',
    status: 'unknown',
    latency: null,
    lastChecked: null,
    tokenExpiresIn: 'N/A',
    lastError: null,
    history: [],
  },
  {
    connectorId: 'jira',
    name: 'Jira Software',
    status: 'unknown',
    latency: null,
    lastChecked: null,
    tokenExpiresIn: 'N/A',
    lastError: null,
    history: [],
  },
  {
    connectorId: 'confluence',
    name: 'Confluence',
    status: 'unknown',
    latency: null,
    lastChecked: null,
    tokenExpiresIn: 'N/A',
    lastError: null,
    history: [],
  },
  {
    connectorId: 'github',
    name: 'GitHub',
    status: 'unknown',
    latency: null,
    lastChecked: null,
    tokenExpiresIn: 'N/A',
    lastError: null,
    history: [],
  },
  {
    connectorId: 'slack',
    name: 'Slack',
    status: 'unknown',
    latency: null,
    lastChecked: null,
    tokenExpiresIn: 'N/A',
    lastError: null,
    history: [],
  },
  {
    connectorId: 'gcalendar',
    name: 'Google Calendar',
    status: 'unknown',
    latency: null,
    lastChecked: null,
    tokenExpiresIn: '55 min',
    lastError: null,
    history: [],
  },
  {
    connectorId: 'gdrive',
    name: 'Google Drive',
    status: 'unknown',
    latency: null,
    lastChecked: null,
    tokenExpiresIn: '55 min',
    lastError: null,
    history: [],
  },
  {
    connectorId: 'gmail',
    name: 'Gmail',
    status: 'unknown',
    latency: null,
    lastChecked: null,
    tokenExpiresIn: '55 min',
    lastError: null,
    history: [],
  },
  {
    connectorId: 'gsheets',
    name: 'Google Sheets',
    status: 'unknown',
    latency: null,
    lastChecked: null,
    tokenExpiresIn: '55 min',
    lastError: null,
    history: [],
  },
  {
    connectorId: 'gdocs',
    name: 'Google Docs',
    status: 'unknown',
    latency: null,
    lastChecked: null,
    tokenExpiresIn: '55 min',
    lastError: null,
    history: [],
  },
  {
    connectorId: 'gslides',
    name: 'Google Slides',
    status: 'unknown',
    latency: null,
    lastChecked: null,
    tokenExpiresIn: '55 min',
    lastError: null,
    history: [],
  },
  {
    connectorId: 'gforms',
    name: 'Google Forms',
    status: 'unknown',
    latency: null,
    lastChecked: null,
    tokenExpiresIn: '55 min',
    lastError: null,
    history: [],
  },
]

// ─── Real Health Check Functions ──────────────────────────────────────────────

async function checkJira(): Promise<{
  ok: boolean
  latency: number
  error?: string
  tokenExpiry?: string
}> {
  const start = performance.now()
  try {
    if (!mcpAuth.isConfigured('jira')) {
      return {
        ok: false,
        latency: 0,
        error: 'Credenciales de Jira no configuradas',
      }
    }
    const token = await mcpAuth.getToken('jira')
    const latency = Math.round(performance.now() - start)
    // Token obtained successfully = Jira auth works
    const status = mcpAuth.getStatus()
    return {
      ok: true,
      latency,
      tokenExpiry: status.jira === 'connected' ? '~55 min' : 'N/A',
    }
  } catch (err: any) {
    return {
      ok: false,
      latency: Math.round(performance.now() - start),
      error: err.message || 'Error de conexión con Jira',
    }
  }
}
async function checkConfluence(): Promise<{
  ok: boolean
  latency: number
  error?: string
  tokenExpiry?: string
}> {
  const start = performance.now()
  try {
    if (!mcpAuth.isConfigured('confluence')) {
      return {
        ok: false,
        latency: 0,
        error: 'Credenciales de Confluence no configuradas',
      }
    }
    const token = await mcpAuth.getToken('confluence')
    const latency = Math.round(performance.now() - start)
    const status = mcpAuth.getStatus()
    return {
      ok: true,
      latency,
      tokenExpiry: status.confluence === 'connected' ? '~55 min' : 'N/A',
    }
  } catch (err: any) {
    return {
      ok: false,
      latency: Math.round(performance.now() - start),
      error: err.message || 'Error de conexión con Confluence',
    }
  }
}
async function checkApiGateway(): Promise<{
  ok: boolean
  latency: number
  error?: string
}> {
  const start = performance.now()
  try {
    // Ping the Jira API Gateway base URL
    const response = await fetch(
      'https://h26174vmaa.execute-api.us-east-1.amazonaws.com/mcp',
      {
        method: 'HEAD',
        signal: AbortSignal.timeout(10000),
      },
    )
    const latency = Math.round(performance.now() - start)
    // Even a 401/403 means the gateway is reachable
    return {
      ok: response.status < 500,
      latency,
    }
  } catch (err: any) {
    return {
      ok: false,
      latency: Math.round(performance.now() - start),
      error: err.message || 'API Gateway no responde',
    }
  }
}
async function checkGitHub(): Promise<{
  ok: boolean
  latency: number
  error?: string
  tokenExpiry?: string
}> {
  const start = performance.now()
  try {
    const token = localStorage.getItem('vsm_github_token')
    if (!token) {
      return {
        ok: false,
        latency: 0,
        error: 'Token de GitHub no configurado',
      }
    }
    const response = await fetch('https://api.github.com/user', {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/vnd.github.v3+json',
      },
      signal: AbortSignal.timeout(10000),
    })
    const latency = Math.round(performance.now() - start)
    if (response.ok) {
      return {
        ok: true,
        latency,
        tokenExpiry: 'N/A (PAT)',
      }
    }
    const data = await response.json().catch(() => ({}))
    return {
      ok: false,
      latency,
      error: `${response.status}: ${data.message || response.statusText}`,
    }
  } catch (err: any) {
    return {
      ok: false,
      latency: Math.round(performance.now() - start),
      error: err.message || 'Error de conexión con GitHub',
    }
  }
}
async function checkSlack(): Promise<{
  ok: boolean
  latency: number
  error?: string
}> {
  const start = performance.now()
  try {
    if (!isSlackConnected()) {
      return {
        ok: false,
        latency: 0,
        error: 'Token de Slack no configurado',
      }
    }
    // auth.test is the lightest Slack API call
    const token = localStorage.getItem('vsm_slack_token')
    if (!token)
      return {
        ok: false,
        latency: 0,
        error: 'Token de Slack no configurado',
      }
    const response = await fetch('https://slack.com/api/auth.test', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: `token=${token}`,
    })
    const data = await response.json()
    const latency = Math.round(performance.now() - start)
    if (data.ok) {
      return {
        ok: true,
        latency,
      }
    }
    return {
      ok: false,
      latency,
      error: data.error || 'Error de autenticación con Slack',
    }
  } catch (err: any) {
    return {
      ok: false,
      latency: Math.round(performance.now() - start),
      error: err.message || 'Error de conexión con Slack',
    }
  }
}
async function checkGoogleService(
  serviceName: string,
  testUrl: string,
): Promise<{
  ok: boolean
  latency: number
  error?: string
  tokenExpiry?: string
}> {
  const start = performance.now()
  try {
    const token = getGoogleToken()
    if (!token) {
      return {
        ok: false,
        latency: 0,
        error: `Google no conectado — ${serviceName} requiere autenticación`,
      }
    }
    const response = await fetch(testUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      signal: AbortSignal.timeout(10000),
    })
    const latency = Math.round(performance.now() - start)
    if (response.ok) {
      return {
        ok: true,
        latency,
        tokenExpiry: '~55 min',
      }
    }
    if (response.status === 401) {
      return {
        ok: false,
        latency,
        error: 'Token de Google expirado. Reconectá tu cuenta.',
      }
    }
    return {
      ok: false,
      latency,
      error: `${serviceName} API error: ${response.status}`,
    }
  } catch (err: any) {
    return {
      ok: false,
      latency: Math.round(performance.now() - start),
      error: err.message || `Error de conexión con ${serviceName}`,
    }
  }
}

// Map connectorId → real check function
const HEALTH_CHECKS: Record<
  string,
  () => Promise<{
    ok: boolean
    latency: number
    error?: string
    tokenExpiry?: string
  }>
> = {
  api: checkApiGateway,
  jira: checkJira,
  confluence: checkConfluence,
  github: checkGitHub,
  slack: checkSlack,
  gcalendar: () =>
    checkGoogleService(
      'Calendar',
      'https://www.googleapis.com/calendar/v3/calendars/primary?fields=id',
    ),
  gdrive: () =>
    checkGoogleService(
      'Drive',
      'https://www.googleapis.com/drive/v3/about?fields=user',
    ),
  gmail: () =>
    checkGoogleService(
      'Gmail',
      'https://gmail.googleapis.com/gmail/v1/users/me/profile',
    ),
  gsheets: () =>
    checkGoogleService(
      'Sheets',
      'https://www.googleapis.com/drive/v3/files?q=mimeType%3D%27application%2Fvnd.google-apps.spreadsheet%27&pageSize=1&fields=files(id)',
    ),
  gdocs: () =>
    checkGoogleService(
      'Docs',
      'https://www.googleapis.com/drive/v3/files?q=mimeType%3D%27application%2Fvnd.google-apps.document%27&pageSize=1&fields=files(id)',
    ),
  gslides: () =>
    checkGoogleService(
      'Slides',
      'https://www.googleapis.com/drive/v3/files?q=mimeType%3D%27application%2Fvnd.google-apps.presentation%27&pageSize=1&fields=files(id)',
    ),
  gforms: () =>
    checkGoogleService(
      'Forms',
      'https://www.googleapis.com/drive/v3/files?q=mimeType%3D%27application%2Fvnd.google-apps.form%27&pageSize=1&fields=files(id)',
    ),
}
export function useHealthCheck() {
  const [healthData, setHealthData] = useState<
    Record<string, ConnectorHealthResult>
  >(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        return JSON.parse(stored)
      }
    } catch (e) {
      console.error('Failed to load health history', e)
    }

    // Initialize from defaults if no storage
    const initial: Record<string, ConnectorHealthResult> = {}
    INITIAL_CONNECTORS.forEach((c) => {
      initial[c.connectorId] = c
    })
    return initial
  })
  const [isCheckingAll, setIsCheckingAll] = useState(false)

  // Save to localStorage whenever healthData changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(healthData))
    } catch (e) {
      console.error('Failed to save health history', e)
    }
  }, [healthData])
  const updateConnector = useCallback(
    (id: string, updates: Partial<ConnectorHealthResult>) => {
      setHealthData((prev) => {
        const current = prev[id]
        if (!current) return prev
        const updated = {
          ...current,
          ...updates,
        }

        // If status or latency changed, add to history
        if (
          updates.status &&
          updates.status !== 'checking' &&
          updates.status !== 'unknown'
        ) {
          const newHistoryEntry: HealthHistoryEntry = {
            timestamp: updates.lastChecked || Date.now(),
            status: updates.status,
            latency:
              updates.latency !== undefined ? updates.latency : current.latency,
          }

          // Keep only last 10 entries
          updated.history = [newHistoryEntry, ...current.history].slice(0, 10)
        }
        return {
          ...prev,
          [id]: updated,
        }
      })
    },
    [],
  )
  const runCheck = useCallback(
    async (connectorId: string) => {
      updateConnector(connectorId, {
        status: 'checking',
      })
      const checkFn = HEALTH_CHECKS[connectorId]
      if (!checkFn) {
        updateConnector(connectorId, {
          status: 'disconnected',
          latency: null,
          lastChecked: Date.now(),
          lastError: `No hay verificación configurada para "${connectorId}"`,
        })
        return
      }
      try {
        const result = await checkFn()
        if (result.ok) {
          const isDegraded = result.latency > 2000
          updateConnector(connectorId, {
            status: isDegraded ? 'degraded' : 'connected',
            latency: result.latency,
            lastChecked: Date.now(),
            lastError: null,
            tokenExpiresIn: result.tokenExpiry || null,
          })
        } else {
          updateConnector(connectorId, {
            status: 'disconnected',
            latency: result.latency || null,
            lastChecked: Date.now(),
            lastError: result.error || 'Error desconocido',
            tokenExpiresIn: result.tokenExpiry || null,
          })
        }
      } catch (err: any) {
        updateConnector(connectorId, {
          status: 'disconnected',
          latency: null,
          lastChecked: Date.now(),
          lastError: err.message || 'Error inesperado',
        })
      }
    },
    [updateConnector],
  )
  const runAllChecks = useCallback(async () => {
    setIsCheckingAll(true)
    const promises = Object.keys(healthData).map((id) => runCheck(id))
    await Promise.all(promises)
    setIsCheckingAll(false)
  }, [healthData, runCheck])
  const exportDiagnostics = useCallback(() => {
    const dataStr = JSON.stringify(healthData, null, 2)
    const dataUri =
      'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr)
    const exportFileDefaultName = `vsm-diagnostics-${new Date().toISOString().slice(0, 10)}.json`
    const linkElement = document.createElement('a')
    linkElement.setAttribute('href', dataUri)
    linkElement.setAttribute('download', exportFileDefaultName)
    linkElement.click()
  }, [healthData])
  const healthDataArray = Object.values(healthData)

  // Calculate overall status for sidebar icon
  const checkedConnectors = healthDataArray.filter(
    (c) => c.status !== 'unknown' && c.status !== 'checking',
  )
  const disconnectedCount = checkedConnectors.filter(
    (c) => c.status === 'disconnected',
  ).length
  const degradedCount = checkedConnectors.filter(
    (c) => c.status === 'degraded',
  ).length
  let overallStatus: 'healthy' | 'degraded' | 'critical' = 'healthy'
  if (checkedConnectors.length > 0) {
    if (disconnectedCount > checkedConnectors.length / 2) {
      overallStatus = 'critical'
    } else if (disconnectedCount > 0 || degradedCount > 0) {
      overallStatus = 'degraded'
    }
  }
  return {
    healthData: healthDataArray,
    isCheckingAll,
    runCheck,
    runAllChecks,
    exportDiagnostics,
    overallStatus,
  }
}
